import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { HTMLAttributes, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { ReactComponent as LocationIcon } from "../../../../assets/icons/locationIcon.svg";
import useCarparkStore from "../../../../store/carpark";
import useInfoStateStore from "../../../../store/infoState";
import BaseBreadCrumb from "../../../atom/breadcrumb";
import GalleryButton from "../../galleryButton";
import MonthlyOrderItemCard from "../../monthlyOrderItemCard";

import { FreeMode, Scrollbar } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { TOMORROW } from "../../../../constants";
import { getBookingSelectDateRange } from "../../../../helper/format";
import BaseButton from "../../../atom/baseButton";
import BaseLabel from "../../../atom/baseLabel";
import BaseTitle from "../../../atom/baseTitle";

dayjs.extend(utc);
dayjs.extend(timezone);

const HK_TZ = "Asia/Hong_Kong";

interface ISelectRentDatePage {
  goNext: () => void;
  goBack?: () => void;
  showItemCard?: boolean;
}

type TSelectRentDatePageProps = ISelectRentDatePage &
  HTMLAttributes<HTMLDivElement>;

const SelectRentDatePage: React.FC<TSelectRentDatePageProps> = (props) => {
  const { goNext, goBack, showItemCard, ...otherProps } = props;
  const [startDate, setStartDate] = useState(TOMORROW);

  const selectedMonthlyItem = useInfoStateStore(
    (state) => state.selectedMonthlyItem
  );

  const carparkInfoList = useCarparkStore((state) => state.carparkInfoList);

  const selectedCarparkInfo = carparkInfoList.find(
    (carpark) =>
      carpark.id.toString() === selectedMonthlyItem?.parking_id.toString()
  );

  const startDataInStore = useInfoStateStore(
    (state) => state.selectedStartDate
  );

  const setStartDateToStore = useInfoStateStore(
    (state) => state.setSelectStartDate
  );

  const customGoNext = () => {
    setStartDateToStore(startDate);
    goNext();
  };

  // loop date range by item config (all computed in HK timezone)
  const DAYS: dayjs.Dayjs[] = [];

  const getDefaultStartDate = getBookingSelectDateRange(
    selectedMonthlyItem?.start_date!
  );

  const baseHK = dayjs.tz(getDefaultStartDate, HK_TZ).startOf("day");
  for (let i = 0; i <= (selectedMonthlyItem?.maximum_period ?? 7) - 1; i++) {
    DAYS.push(baseHK.add(i, "day"));
  }

  useEffect(() => {
    setStartDate(getDefaultStartDate);
  }, []);

  useEffect(() => {
    if (startDataInStore) {
      setStartDate(startDataInStore);
    }
  }, [startDataInStore]);

  return (
    <div className="flex flex-1 flex-col gap-4" {...otherProps}>
      {goBack && <BaseBreadCrumb onClick={goBack}>{`<上一頁`}</BaseBreadCrumb>}
      {selectedCarparkInfo && (
        <BaseTitle text={selectedCarparkInfo?.display_name || ""} />
      )}
      {selectedCarparkInfo && (
        <div className="flex flex-row justify-between items-center">
          <a
            className="link flex flex-row items-center text-lg gap-2"
            href={selectedCarparkInfo?.map}
            target="_blank"
            rel="noreferrer"
          >
            <LocationIcon /> {selectedCarparkInfo?.location}
          </a>

          <GalleryButton />
        </div>
      )}

      <MonthlyOrderItemCard
        key={selectedMonthlyItem?.id}
        item={selectedMonthlyItem!}
        hideQuota
        showItemCard={showItemCard}
      />

      <BaseLabel text="選擇開始日期" />

      <Swiper
        className="flex w-full pb-4 [&_.swiper-scrollbar]:h-1 rounded-lg [&_.swiper-scrollbar-drag]:bg-mainYellow"
        slidesPerView={7}
        spaceBetween={10}
        freeMode={true}
        modules={[Scrollbar, FreeMode]}
        scrollbar={{
          hide: false,
          draggable: true,
        }}
      >
        {DAYS.map((day, index) => {
          // compare in HK timezone
          const _isSelected = dayjs.tz(startDate, HK_TZ).isSame(day, "day");
          return (
            <SwiperSlide key={index}>
              <div
                className={`group w-10 h-10 flex flex-col justify-center items-center font-bold rounded-full cursor-pointer hover:bg-mainYellow hover:bg-opacity-50 ${
                  _isSelected ? "bg-mainYellow text-mainDark" : "text-white"
                }`}
                onClick={() => setStartDate(dayjs(day).format("YYYY-MM-DD"))}
              >
                <div className="text-sm leading-4">
                  {dayjs(day).format("DD")}
                </div>
                <div className="text-xs leading-3">
                  {dayjs(day).format("MMM")}
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className="flex flex-col gap-1">
        <BaseLabel text="起租日" />
        <div className="text-white font-bold">
          {dayjs.tz(startDate, HK_TZ).format("YYYY年MM月D日")}
        </div>
      </div>
      <BaseButton onClick={customGoNext} minWidth="w-full">
        下一步
      </BaseButton>
    </div>
  );
};

export default SelectRentDatePage;
